import {Badge, Popover} from 'antd'
import classNames from 'classnames'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useLayoutEffect, useRef, useState} from 'react'
import {FiMoreHorizontal} from 'react-icons/fi'
import {LoginContext} from '../../../components/LoginContext'

const MORE_BTN_WIDTH = 44

export const TabNavigation = ({
                                  tabs = [],
                                  activeTab,
                                  onTabChange,
                                  className,
                                  style,
                              }) => {
    const t = useTranslate()
    const login = useContext(LoginContext)
    const outerRef = useRef(null)
    const [visibleCount, setVisibleCount] = useState(tabs.length)
    const [moreOpen, setMoreOpen] = useState(false)

    useLayoutEffect(() => {
        const outer = outerRef.current
        if (!outer) return

        const compute = () => {
            const tabEls = Array.from(outer.querySelectorAll('[data-tab-index]'))
            if (!tabEls.length) return

            const outerW = outer.clientWidth
            const lastEl = tabEls[tabEls.length - 1]
            const totalRight = lastEl.offsetLeft + lastEl.offsetWidth

            if (totalRight <= outerW) {
                setVisibleCount(tabEls.length)
                return
            }

            const avail = outerW - MORE_BTN_WIDTH
            let count = 0
            for (const el of tabEls) {
                if (el.offsetLeft + el.offsetWidth <= avail) {
                    count++
                } else {
                    break
                }
            }
            setVisibleCount(Math.max(1, count))
        }

        compute()
        const ro = new ResizeObserver(compute)
        ro.observe(outer)
        return () => ro.disconnect()
    }, [tabs])

    const overflowTabs = tabs.slice(visibleCount)
    const hasMore = overflowTabs.length > 0
    const activeInOverflow = overflowTabs.some((tab) => tab.key === activeTab)

    const moreContent = (
        <div className="flex flex-col min-w-[160px]">
            {overflowTabs.map((tab) => (
                <button
                    key={tab.key}
                    onClick={() => {
                        onTabChange(tab.key)
                        setMoreOpen(false)
                    }}
                    className={classNames(
                        'flex items-center gap-2 px-3 py-2.5 text-sm rounded-md transition-colors text-left w-full',
                        activeTab === tab.key
                            ? 'text-indigo-600 bg-indigo-50'
                            : 'text-color-200 hover:text-indigo-500 hover:bg-color-50'
                    )}>
                    {tab?.icon}
                    <span>{t(tab.name)}</span>
                </button>
            ))}
        </div>
    )

    return (
        <div
            style={style}
            className={classNames(
                'flex background-100 rounded-t-xl border-b sticky',
                className
            )}>
            <div
                ref={outerRef}
                className="flex gap-1 overflow-hidden overflow-x-auto scrollBarMedium flex-1">
                {tabs.map((tab, i) => {
                    const isOverflow = i >= visibleCount
                    return (
                        <button
                            key={tab.key}
                            data-tab-index={i}
                            aria-hidden={isOverflow || undefined}
                            tabIndex={isOverflow ? -1 : undefined}
                            onClick={() => !isOverflow && onTabChange(tab.key)}
                            className={classNames(
                                'flex items-center flex-shrink-0 gap-2 px-3 py-3 text-sm font-medium border-b-2 transition-colors duration-300',
                                isOverflow && 'pointer-events-none',
                                activeTab === tab.key
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-color-200 hover:text-indigo-500 hover:border-indigo-300'
                            )}>
                            {tab?.count ? (
                                <Badge
                                    dot
                                    showZero={false}
                                    className="custom-badge-color"
                                    count={_.get(login, tab.count, 0)}>
                                    {tab?.icon}
                                </Badge>
                            ) : (
                                tab?.icon
                            )}
                            <span>{t(tab.name)}</span>
                        </button>
                    )
                })}
            </div>
            {hasMore && (
                <Popover
                    open={moreOpen}
                    onOpenChange={setMoreOpen}
                    content={moreContent}
                    trigger="click"
                    placement="bottomRight"
                    destroyTooltipOnHide>
                    <button
                        className={classNames(
                            'px-2 flex-shrink-0 flex items-center justify-center self-stretch transition-colors duration-300',
                            activeInOverflow
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-color-300 hover:text-indigo-500 hover:bg-color-50'
                        )}>
                        <FiMoreHorizontal size={18}/>
                    </button>
                </Popover>
            )}
        </div>
    )
}

export default TabNavigation
